import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Sparkles, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const verdictTone = {
  BUY: 'bg-emerald-500/15 text-emerald-300 border-emerald-700/40',
  ADD: 'bg-emerald-500/15 text-emerald-300 border-emerald-700/40',
  HOLD: 'bg-slate-500/15 text-slate-300 border-slate-600/40',
  PASS: 'bg-slate-500/15 text-slate-300 border-slate-600/40',
  TRIM: 'bg-amber-500/15 text-amber-300 border-amber-700/40',
  SELL: 'bg-rose-500/15 text-rose-300 border-rose-700/40',
};

const regimeTone = {
  RISK_ON: 'text-emerald-400',
  NEUTRAL: 'text-slate-400',
  RISK_OFF: 'text-amber-400',
};

/** Public daily-pick page. Free to read. */
export default function OneStockPage() {
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [t, h] = await Promise.all([
          axios.get(`${API}/daily-pick/today`),
          axios.get(`${API}/daily-pick/history`, { params: { limit: 14 } }),
        ]);
        if (cancelled) return;
        setToday(t.data);
        setHistory(h.data?.picks || []);
      } catch (_) {
        /* keep nulls; UI shows empty state */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  const noPick = !today || today.status !== 'ok';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-4 py-1.5 text-xs font-medium text-emerald-300 mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            ONE STOCK A DAY
          </div>
          <h1 className="text-4xl font-bold">Today's KubAIr pick</h1>
          <p className="mt-3 text-slate-400">
            One stock per trading day, picked by our scanner and run through the full
            6-analyst committee. Fully transparent track record below.
          </p>
        </div>

        {noPick ? (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-10 text-center text-slate-400">
            No pick published yet today. Check back after market hours.
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900/80 to-slate-950 p-8 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs text-slate-500">{today.pick_date}</div>
                <div className="text-3xl font-bold mt-1">{today.nse_symbol}</div>
                <div className="text-sm text-slate-400">{today.company_name}</div>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded text-sm font-bold border ${verdictTone[today.verdict] || ''}`}>
                {today.verdict} · {today.conviction}/10
              </span>
            </div>
            <p className="text-base text-slate-200 leading-relaxed mb-6">
              {today.one_line_thesis}
            </p>
            <div className="flex flex-wrap gap-4 text-xs">
              {today.regime_label && (
                <div>
                  <span className="text-slate-500">Regime: </span>
                  <span className={regimeTone[today.regime_label] || 'text-slate-300'}>
                    {today.regime_label}
                  </span>
                </div>
              )}
              {today.selector_score !== null && (
                <div>
                  <span className="text-slate-500">Selector score: </span>
                  <span className="text-slate-300">{Number(today.selector_score).toFixed(2)}</span>
                </div>
              )}
              {today.selector_reason && (
                <div className="text-slate-500 w-full">{today.selector_reason}</div>
              )}
            </div>
          </div>
        )}

        {/* Track record */}
        {history.length > 0 && (
          <div className="mt-12">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              Recent picks
            </h2>
            <div className="rounded-xl border border-slate-800 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/60 text-xs text-slate-400">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Date</th>
                    <th className="text-left px-4 py-3 font-medium">Stock</th>
                    <th className="text-left px-4 py-3 font-medium">Verdict</th>
                    <th className="text-left px-4 py-3 font-medium">Regime</th>
                    <th className="text-left px-4 py-3 font-medium">Thesis</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.pick_date} className="border-t border-slate-800/60">
                      <td className="px-4 py-3 text-slate-400 text-xs">{h.pick_date}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{h.nse_symbol}</div>
                        <div className="text-[11px] text-slate-500">{h.company_name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${verdictTone[h.verdict] || ''}`}>
                          {h.verdict} · {h.conviction}/10
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className={regimeTone[h.regime_label] || 'text-slate-400'}>
                          {h.regime_label || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs max-w-md">{h.one_line_thesis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="mt-10 text-center text-[11px] text-slate-500">
          For information only. Not investment advice. Past performance does not guarantee
          future returns.
        </p>
      </div>
    </div>
  );
}
